import * as React from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError, type PaymentRecordInput } from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@alight/ui";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import {
  formatAud,
  formatDateTime,
  PaymentHistory,
  RecordPaymentDialog,
  RECON_STATUS_BADGE,
  RECON_STATUS_LABEL,
} from "../components/ReconcilePayments";
import { SummaryDoneButton } from "./reconcile";

/**
 * /reconcile/$ref — one payment record: the money position, the ledger and
 * audit history, and the "record payment" action (partial or full). This is
 * where the account manager's emailed magic links land.
 */

const api = apiClient("");

export function ReconcileRecord() {
  const { ref } = useParams({ strict: false }) as { ref: string };
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["reconcile", "detail", ref],
    queryFn: () => api.reconcileGet(ref),
    retry: (count, err) =>
      !(err instanceof ApiError && [401, 403, 404].includes(err.status)) && count < 2,
  });

  const [recording, setRecording] = React.useState(false);
  const record = useMutation({
    mutationFn: (input: PaymentRecordInput) => api.reconcileRecordPayment(ref, input),
    onSuccess: (fresh) => {
      qc.setQueryData(["reconcile", "detail", ref], fresh);
      void qc.invalidateQueries({ queryKey: ["reconcile", "enrolments"] });
      setRecording(false);
    },
  });

  if (detail.isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading record…
      </div>
    );
  }

  if (detail.isError) {
    const status = detail.error instanceof ApiError ? detail.error.status : 0;
    return (
      <Card>
        <CardContent className="flex items-start gap-3 p-6 text-sm">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
          <div>
            <p className="font-semibold text-ink">
              {status === 404
                ? `No payment record found for ${ref}`
                : status === 401 || status === 403
                  ? "You're not authorised to view this record"
                  : "Couldn't load this record"}
            </p>
            <Button asChild size="sm" variant="outline" className="mt-3">
              <Link to="/reconcile">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to the list
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const d = detail.data!;
  const e = d.enrolment;
  const balance = Math.max(e.balance_cents, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild size="sm" variant="ghost">
          <Link to="/reconcile">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All payments
          </Link>
        </Button>
        <SummaryDoneButton />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle className="font-mono">{e.payment_ref}</CardTitle>
            <Badge variant={RECON_STATUS_BADGE[e.payment_status]}>
              {RECON_STATUS_LABEL[e.payment_status]}
            </Badge>
          </div>
          <CardDescription>
            {e.parent_name} ·{" "}
            <a href={`mailto:${e.parent_email}`} className="hover:text-primary">
              {e.parent_email}
            </a>
            {e.parent_phone ? ` · ${e.parent_phone}` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* A family pays ONCE under one reference, so the bank line in front
              of you may be far larger than this child's fee. Say so here
              rather than letting it read as an overpayment. */}
          {d.invoice && d.invoice.children > 1 && (
            <div className="rounded-card border border-primary/30 bg-primary/5 p-4">
              <p className="text-sm font-semibold text-ink">
                Part of {d.invoice.number} — {d.invoice.children} children,{" "}
                {formatAud(d.invoice.total_cents)} total
              </p>
              <p className="mt-1 text-sm text-muted">
                One payment covers all of these. Record it against each child.
              </p>
              <ul className="mt-3 divide-y divide-border border-t border-border">
                {d.invoice.lines.map((line) => (
                  <li
                    key={line.enrolment_id}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 py-1.5 text-sm"
                  >
                    <span className="text-ink">{line.description}</span>
                    <span className="flex items-baseline gap-2">
                      <span className="tabular-nums text-muted">
                        {formatAud(line.amount_cents)}
                      </span>
                      <Badge variant={line.payment_status === "paid" ? "outline" : "accent"}>
                        {line.payment_status === "paid" ? "paid" : "outstanding"}
                      </Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted">Child</dt>
              <dd className="font-medium text-ink">{e.child_name}</dd>
            </div>
            <div>
              <dt className="text-muted">Year level</dt>
              <dd className="font-medium text-ink">{e.year_level}</dd>
            </div>
            <div>
              <dt className="text-muted">Payment method</dt>
              <dd className="font-medium text-ink">{e.payment_method ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted">Flagged sent</dt>
              <dd className="font-medium text-ink">{formatDateTime(e.intent_at)}</dd>
            </div>
            <div>
              <dt className="text-muted">Fee</dt>
              <dd className="font-semibold text-ink">{formatAud(e.amount_cents)}</dd>
            </div>
            <div>
              <dt className="text-muted">Received</dt>
              <dd className="font-semibold text-ink">{formatAud(e.received_cents)}</dd>
            </div>
            <div>
              <dt className="text-muted">Balance</dt>
              <dd className="font-semibold text-ink">{formatAud(balance)}</dd>
            </div>
            {e.paid_at && (
              <div>
                <dt className="text-muted">Paid at</dt>
                <dd className="font-medium text-ink">{formatDateTime(e.paid_at)}</dd>
              </div>
            )}
          </dl>

          {e.payment_status !== "paid" && (
            <Button type="button" variant="accent" onClick={() => setRecording(true)}>
              Record payment received
            </Button>
          )}

          <PaymentHistory payments={d.payments} events={d.events} />
        </CardContent>
      </Card>

      <RecordPaymentDialog
        enrolment={recording ? e : null}
        onOpenChange={(open) => {
          if (!open) {
            setRecording(false);
            record.reset();
          }
        }}
        onSubmit={(input) => record.mutate(input)}
        pending={record.isPending}
        error={record.error}
      />
    </div>
  );
}
